import React, { useState, useCallback } from 'react';
import { Search, Download, Calendar, User, FileText, Loader2, Package } from 'lucide-react';
import { apiService, User as ApiUser, Domain as ApiDomain } from '../services/api';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';

export const MassInvoice: React.FC = () => {
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [foundUser, setFoundUser] = useState<ApiUser | null>(null);
  const [userDomains, setUserDomains] = useState<ApiDomain[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number; currentUser: string } | null>(null);
  const [bulkLimit, setBulkLimit] = useState(50);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [domainsError, setDomainsError] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<{ 
    currentBatch: number; 
    totalBatches: number; 
    batchSize: number;
    completedZips: string[];
  } | null>(null);
  const [calculationDetails, setCalculationDetails] = useState<{
    totalDomains: number;
    domainsWithEmails: number;
    totalAccountsToCreate: number;
    totalAccountsToCancel: number;
    accountsCanceledBeforeMonth: number;
    accountsCanceledInMonth: number;
    finalEmailCount: number;
    details: Array<{
      domainName: string;
      created: string;
      accountsToCreate: number;
      accountsToCancel: number;
      canceledDate: string | null;
      effectiveCount: number;
      reason: string;
    }>;
  } | null>(null);

  // Initialize selected month to current month
  React.useEffect(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
    setSelectedMonth(currentMonth);
  }, []);

  const searchUserByEmail = useCallback(async () => {
    if (!searchEmail.trim()) {
      setSearchError('Please enter an email address');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setFoundUser(null);
    setUserDomains([]);
    setDomainsError(null);

    try {
      // Search for user using the existing API with email search
      const response = await apiService.getUsers({
        search: searchEmail.trim(),
        limit: 1,
        page: 1
      });

      if (response.data && response.data.length > 0) {
        // Find exact email match
        const exactMatch = response.data.find(user => 
          user.email.toLowerCase() === searchEmail.trim().toLowerCase()
        );
        
        if (exactMatch) {
          setFoundUser(exactMatch);
          // Fetch domains for this user
          await fetchUserDomains(exactMatch.id);
        } else {
          setSearchError('No user found with the exact email address');
        }
      } else {
        setSearchError('No user found with this email address');
      }
    } catch (error) {
      console.error('Error searching for user:', error);
      setSearchError('Error searching for user. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [searchEmail]);

  const fetchUserDomains = async (userId: string) => {
    try {
      const response = await apiService.getUserDomains(userId);
      setUserDomains(response.data || []);
    } catch (error) {
      console.error('Error fetching user domains:', error);
      setDomainsError('Error fetching user domains');
    }
  };

  // Helper function to parse and extract emails from a field
  const extractEmailsFromField = (emailField: string | undefined | null): string[] => {
    if (!emailField || emailField === '' || emailField === '[]' || emailField === 'null' || emailField === 'undefined') return [];
    
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(emailField);
      if (Array.isArray(parsed)) {
        return parsed.filter(email => email && typeof email === 'string' && email.trim().includes('@'))
                    .map(email => email.trim().toLowerCase());
      }
      if (typeof parsed === 'object') {
        return Object.values(parsed).filter(v => v && typeof v === 'string' && v.trim().includes('@'))
                                   .map(v => (v as string).trim().toLowerCase());
      }
      if (typeof parsed === 'string' && parsed.trim().includes('@')) {
        return [parsed.trim().toLowerCase()];
      }
      return [];
    } catch {
      // If not JSON, try splitting by comma
      return emailField
        .split(',')
        .map(email => email.trim().toLowerCase())
        .filter(email => email.length > 0 && email.includes('@'));
    }
  };

  // Helper function to parse and count emails from a field (copied from UserDetail.tsx)
  const countEmailsInField = (emailField: string | undefined | null): number => {
    return extractEmailsFromField(emailField).length;
  };

  // Helper function to count unique emails between create and cancel fields
  const countUniqueEmails = (accountsToCreate: string | undefined | null, accountsToCancel: string | undefined | null): { 
    totalUnique: number; 
    createOnly: number; 
    cancelOnly: number; 
    overlap: number; 
  } => {
    const createEmails = new Set(extractEmailsFromField(accountsToCreate));
    const cancelEmails = new Set(extractEmailsFromField(accountsToCancel));
    
    const allEmails = new Set([...Array.from(createEmails), ...Array.from(cancelEmails)]);
    const overlap = Array.from(createEmails).filter(email => cancelEmails.has(email)).length;
    
    return {
      totalUnique: allEmails.size,
      createOnly: createEmails.size - overlap,
      cancelOnly: cancelEmails.size - overlap,
      overlap: overlap
    };
  };

  const calculateEmailAccountsForMonth = useCallback((domains: ApiDomain[], targetMonth: string): number => {
    if (!targetMonth) return 0;

    const [year, month] = targetMonth.split('-').map(Number);
    const startOfMonth = new Date(year, month - 1, 1);
    const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);

    let totalEmailAccounts = 0;

    domains.forEach((domain, index) => {
      const domainDate = new Date(domain.created_at);
      
      // Skip domains created after the target month
      if (domainDate > endOfMonth) {
        return;
      }

      const accountsToCreate = countEmailsInField(domain.accounts_to_create);
      const accountsToCancelCount = countEmailsInField(domain.accounts_to_cancel);

      let effectiveEmailCount = accountsToCreate;

      // Handle domain-level cancellation
      if (domain.canceled_date) {
        const canceledDate = new Date(domain.canceled_date);
        const canceledMonth = canceledDate.getMonth() + 1;
        const canceledYear = canceledDate.getFullYear();
        
        // If canceled before the target month, exclude this domain entirely
        if (canceledYear < year || (canceledYear === year && canceledMonth < month)) {
          return;
        }
      }

      // Handle account-level cancellation (accounts_to_cancel)
      if (accountsToCancelCount > 0 && domain.canceled_date) {
        const canceledDate = new Date(domain.canceled_date);
        const canceledMonth = canceledDate.getMonth() + 1;
        const canceledYear = canceledDate.getFullYear();
        
        // Use deduplication logic to handle overlapping emails
        const emailCounts = countUniqueEmails(domain.accounts_to_create, domain.accounts_to_cancel);
        
        // Only subtract canceled accounts if they were canceled BEFORE the target month
        if (canceledYear < year || (canceledYear === year && canceledMonth < month)) {
          // For past cancellations, count only the currently active accounts (create - cancel, but avoid double-counting)
          effectiveEmailCount = Math.max(0, emailCounts.createOnly);
        }
        // If canceled IN the target month, count all unique emails (both created and canceled were active during the month)
        else if (canceledYear === year && canceledMonth === month) {
          effectiveEmailCount = emailCounts.totalUnique;
        }
        // If canceled AFTER the target month, count all unique emails (they were all active during the target month)
        else {
          effectiveEmailCount = emailCounts.totalUnique;
        }
      }

      totalEmailAccounts += effectiveEmailCount;
    });

    return totalEmailAccounts;
  }, []);

  // Separate function to calculate details for debugging (called via useEffect)
  const calculateDetailsForMonth = useCallback((domains: ApiDomain[], targetMonth: string) => {
    if (!targetMonth) return;

    const [year, month] = targetMonth.split('-').map(Number);
    const startOfMonth = new Date(year, month - 1, 1);
    const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);

    let totalAccountsToCreate = 0;
    let totalAccountsToCancel = 0;
    let accountsCanceledBeforeMonth = 0;
    let accountsCanceledInMonth = 0;
    let domainsWithEmails = 0;
    let finalEmailCount = 0;
    const details: Array<{
      domainName: string;
      created: string;
      accountsToCreate: number;
      accountsToCancel: number;
      canceledDate: string | null;
      effectiveCount: number;
      reason: string;
    }> = [];

    domains.forEach((domain, index) => {
      const domainDate = new Date(domain.created_at);
      
      // Skip domains created after the target month
      if (domainDate > endOfMonth) {
        return;
      }

      const accountsToCreate = countEmailsInField(domain.accounts_to_create);
      const accountsToCancelCount = countEmailsInField(domain.accounts_to_cancel);
      
      totalAccountsToCreate += accountsToCreate;
      totalAccountsToCancel += accountsToCancelCount;

      let effectiveEmailCount = accountsToCreate;
      let reason = 'Active accounts';

      // Handle domain-level cancellation
      if (domain.canceled_date) {
        const canceledDate = new Date(domain.canceled_date);
        const canceledMonth = canceledDate.getMonth() + 1;
        const canceledYear = canceledDate.getFullYear();
        
        // If canceled before the target month, exclude this domain entirely
        if (canceledYear < year || (canceledYear === year && canceledMonth < month)) {
          effectiveEmailCount = 0;
          reason = 'Domain canceled before target month';
          details.push({
            domainName: domain.domain_name,
            created: domain.created_at,
            accountsToCreate,
            accountsToCancel: accountsToCancelCount,
            canceledDate: domain.canceled_date,
            effectiveCount: effectiveEmailCount,
            reason
          });
          return;
        }
        // If canceled in the same month as target month, still include it (bill for the full month)
        else if (canceledYear === year && canceledMonth === month) {
          reason = 'Domain canceled in target month - billing full month';
        }
        // If canceled after target month, include it
        else {
          reason = 'Domain canceled after target month - still active';
        }
      }

      // Handle account-level cancellation (accounts_to_cancel)
      if (accountsToCancelCount > 0 && domain.canceled_date) {
        const canceledDate = new Date(domain.canceled_date);
        const canceledMonth = canceledDate.getMonth() + 1;
        const canceledYear = canceledDate.getFullYear();
        
        // Use deduplication logic to handle overlapping emails
        const emailCounts = countUniqueEmails(domain.accounts_to_create, domain.accounts_to_cancel);
        
        // Only subtract canceled accounts if they were canceled BEFORE the target month
        if (canceledYear < year || (canceledYear === year && canceledMonth < month)) {
          effectiveEmailCount = Math.max(0, emailCounts.createOnly);
          accountsCanceledBeforeMonth += accountsToCancelCount;
          reason = `${accountsToCreate} create, ${accountsToCancelCount} cancel (${emailCounts.overlap} overlap) = ${effectiveEmailCount} active`;
        } else if (canceledYear === year && canceledMonth === month) {
          // Canceled in the same month - count all unique emails (deduplicated)
          effectiveEmailCount = emailCounts.totalUnique;
          accountsCanceledInMonth += accountsToCancelCount;
          reason = `${accountsToCreate} create + ${accountsToCancelCount} cancel (${emailCounts.overlap} overlap) = ${effectiveEmailCount} unique total`;
        } else {
          // Canceled after target month - count all unique emails (they were all active during target month)
          effectiveEmailCount = emailCounts.totalUnique;
          reason = `${accountsToCreate} create + ${accountsToCancelCount} future cancel (${emailCounts.overlap} overlap) = ${effectiveEmailCount} unique total`;
        }
      } else if (accountsToCancelCount > 0) {
        reason = `${accountsToCreate} accounts, ${accountsToCancelCount} marked for cancellation (no date)`;
      }

      if (effectiveEmailCount > 0) {
        domainsWithEmails++;
      }

      details.push({
        domainName: domain.domain_name,
        created: domain.created_at,
        accountsToCreate,
        accountsToCancel: accountsToCancelCount,
        canceledDate: domain.canceled_date || null,
        effectiveCount: effectiveEmailCount,
        reason
      });

      finalEmailCount += effectiveEmailCount;
    });

    setCalculationDetails({
      totalDomains: domains.filter(d => new Date(d.created_at) <= endOfMonth).length,
      domainsWithEmails,
      totalAccountsToCreate,
      totalAccountsToCancel,
      accountsCanceledBeforeMonth,
      accountsCanceledInMonth,
      finalEmailCount,
      details
    });
  }, []);

  // Update calculation details when dependencies change
  React.useEffect(() => {
    if (foundUser && selectedMonth && userDomains.length > 0) {
      calculateDetailsForMonth(userDomains, selectedMonth);
    }
  }, [foundUser, selectedMonth, userDomains, calculateDetailsForMonth]);

  const calculateDomainsForMonth = useCallback((domains: ApiDomain[], targetMonth: string): number => {
    if (!targetMonth) return 0;

    const [year, month] = targetMonth.split('-').map(Number);
    const startOfMonth = new Date(year, month - 1, 1);
    const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);

    return domains.filter(domain => {
      const domainDate = new Date(domain.created_at);
      return domainDate >= startOfMonth && domainDate <= endOfMonth;
    }).length;
  }, []);

  const generateInvoicePDF = useCallback(() => {
    if (!foundUser || !selectedMonth) return;

    setIsGenerating(true);

    try {
      const totalEmailAccounts = calculateEmailAccountsForMonth(userDomains, selectedMonth);
      const currentMonthDomains = calculateDomainsForMonth(userDomains, selectedMonth);
      const emailCost = totalEmailAccounts * 4;
      const domainCost = currentMonthDomains * 12;
      const totalCost = emailCost + domainCost;

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Colors
      const primaryColor = [0, 0, 0];
      const lightGray = [248, 250, 252];
      const mediumGray = [156, 163, 175];

      // Reset text color
      doc.setTextColor(0, 0, 0);

      // Company info section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('FROM:', 20, 30);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text('Far, LLC', 20, 42);
      doc.text('169 Madison Ave STE 2601', 20, 52);
      doc.text('New York, New York 10016', 20, 62);
      doc.text('United States', 20, 72);

      // Invoice details section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('INVOICE DETAILS:', pageWidth - 20, 30, { align: 'right' });

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const invoiceDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const invoiceNumber = `INV-${Date.now()}`;
      const [year, month] = selectedMonth.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      doc.text(`Date: ${invoiceDate}`, pageWidth - 20, 42, { align: 'right' });
      doc.text(`Invoice #: ${invoiceNumber}`, pageWidth - 20, 52, { align: 'right' });
      doc.text(`Period: ${monthName}`, pageWidth - 20, 62, { align: 'right' });
      doc.text(`Due: Net 30`, pageWidth - 20, 72, { align: 'right' });

      // Bill to section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('BILL TO:', 20, 95);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(foundUser.email, 20, 107);
      doc.text(`Workspace ID: ${foundUser.workspace_id || 'N/A'}`, 20, 117);
      doc.text(`Client ID: ${foundUser.client_id || 'N/A'}`, 20, 127);

      // Services table header
      const tableTop = 145;
      const tableHeight = 28;

      // Table header background
      doc.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
      doc.rect(20, tableTop, pageWidth - 40, tableHeight, 'F');

      // Table border
      doc.setDrawColor(mediumGray[0], mediumGray[1], mediumGray[2]);
      doc.setLineWidth(0.5);
      doc.rect(20, tableTop, pageWidth - 40, tableHeight);

      // Table headers
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(55, 65, 81);
      doc.text('DESCRIPTION', 25, tableTop + 10);
      doc.text('QTY', 110, tableTop + 10);
      doc.text('RATE', 135, tableTop + 10);
      doc.text('AMOUNT', pageWidth - 25, tableTop + 10, { align: 'right' });

      // Line separator
      doc.setLineWidth(0.8);
      doc.line(20, tableTop + tableHeight, pageWidth - 20, tableTop + tableHeight);

      let currentY = tableTop + tableHeight + 18;

      // Email accounts line item
      if (totalEmailAccounts > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Business Workspace License', 25, currentY);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Monthly service for email infrastructure (${monthName})`, 25, currentY + 10);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(totalEmailAccounts.toString(), 110, currentY);
        doc.text('$4.00', 135, currentY);
        doc.text(`$${emailCost.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });

        currentY += 30;
      }

      // Domains line item
      if (currentMonthDomains > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Domain DNS Management', 25, currentY);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Annual service for domains (${monthName})`, 25, currentY + 10);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(currentMonthDomains.toString(), 110, currentY);
        doc.text('$12.00', 135, currentY);
        doc.text(`$${domainCost.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });

        currentY += 35;
      }

      // Add space before totals
      currentY += 20;

      // Check if we need a new page (leave space for subtotal and total sections)
      const remainingSpace = pageHeight - currentY;
      const neededSpace = 80; // Space needed for subtotal and total sections
      
      if (remainingSpace < neededSpace) {
        doc.addPage();
        currentY = 40; // Reset Y position on new page with some top margin
      }

      // Subtotal and total section
      doc.setDrawColor(mediumGray[0], mediumGray[1], mediumGray[2]);
      doc.setLineWidth(0.5);
      doc.line(110, currentY, pageWidth - 20, currentY);

      currentY += 18;

      // Subtotal
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text('Subtotal:', 140, currentY);
      doc.text(`$${totalCost.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });

      currentY += 20;

      // Total with black background
      doc.setFillColor(0, 0, 0);
      doc.rect(110, currentY - 10, pageWidth - 130, 25, 'F');

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('TOTAL:', 140, currentY + 5);
      doc.text(`$${totalCost.toFixed(2)}`, pageWidth - 25, currentY + 5, { align: 'right' });

      // Download the PDF
      const filename = `pager-invoice-${foundUser?.workspace_id || 'unknown'}-${selectedMonth}.pdf`;
      doc.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [foundUser, selectedMonth, userDomains, calculateEmailAccountsForMonth, calculateDomainsForMonth]);

  // Helper function to generate a single PDF for a user (returns the PDF blob)
  const generateSingleInvoicePDF = useCallback(async (user: ApiUser, domains: ApiDomain[], targetMonth: string, invoiceCounter: number): Promise<{ filename: string; blob: Blob } | null> => {
    try {
      const totalEmailAccounts = calculateEmailAccountsForMonth(domains, targetMonth);
      const currentMonthDomains = calculateDomainsForMonth(domains, targetMonth);
      
      // Skip users with no billable items
      if (totalEmailAccounts === 0 && currentMonthDomains === 0) {
        return null;
      }

      const emailCost = totalEmailAccounts * 4;
      const domainCost = currentMonthDomains * 12;
      const totalCost = emailCost + domainCost;

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Colors
      const primaryColor = [0, 0, 0];
      const lightGray = [248, 250, 252];
      const mediumGray = [156, 163, 175];

      // Reset text color
      doc.setTextColor(0, 0, 0);

      // Company info section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('FROM:', 20, 30);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text('Far, LLC', 20, 42);
      doc.text('169 Madison Ave STE 2601', 20, 52);
      doc.text('New York, New York 10016', 20, 62);
      doc.text('United States', 20, 72);

      // Invoice details section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('INVOICE DETAILS:', pageWidth - 20, 30, { align: 'right' });

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const invoiceDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      // Create unique invoice number using timestamp + counter + user ID
      const baseTimestamp = Date.now();
      const invoiceNumber = `INV-${baseTimestamp.toString().slice(-8)}-${invoiceCounter.toString().padStart(4, '0')}-${user.id.slice(-4)}`;
      const [year, month] = targetMonth.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      doc.text(`Date: ${invoiceDate}`, pageWidth - 20, 42, { align: 'right' });
      doc.text(`Invoice #: ${invoiceNumber}`, pageWidth - 20, 52, { align: 'right' });
      doc.text(`Period: ${monthName}`, pageWidth - 20, 62, { align: 'right' });
      doc.text(`Due: Net 30`, pageWidth - 20, 72, { align: 'right' });

      // Bill to section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('BILL TO:', 20, 95);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(user.email, 20, 107);
      doc.text(`Workspace ID: ${user.workspace_id || 'N/A'}`, 20, 117);
      doc.text(`Client ID: ${user.client_id || 'N/A'}`, 20, 127);

      // Services table header
      const tableTop = 145;
      const tableHeight = 28;

      // Table header background
      doc.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
      doc.rect(20, tableTop, pageWidth - 40, tableHeight, 'F');

      // Table border
      doc.setDrawColor(mediumGray[0], mediumGray[1], mediumGray[2]);
      doc.setLineWidth(0.5);
      doc.rect(20, tableTop, pageWidth - 40, tableHeight);

      // Table headers
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(55, 65, 81);
      doc.text('DESCRIPTION', 25, tableTop + 10);
      doc.text('QTY', 110, tableTop + 10);
      doc.text('RATE', 135, tableTop + 10);
      doc.text('AMOUNT', pageWidth - 25, tableTop + 10, { align: 'right' });

      // Line separator
      doc.setLineWidth(0.8);
      doc.line(20, tableTop + tableHeight, pageWidth - 20, tableTop + tableHeight);

      let currentY = tableTop + tableHeight + 18;

      // Email accounts line item
      if (totalEmailAccounts > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Business Workspace License', 25, currentY);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Monthly service for email infrastructure (${monthName})`, 25, currentY + 10);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(totalEmailAccounts.toString(), 110, currentY);
        doc.text('$4.00', 135, currentY);
        doc.text(`$${emailCost.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });

        currentY += 30;
      }

      // Domains line item
      if (currentMonthDomains > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Domain DNS Management', 25, currentY);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Annual service for domains (${monthName})`, 25, currentY + 10);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(currentMonthDomains.toString(), 110, currentY);
        doc.text('$12.00', 135, currentY);
        doc.text(`$${domainCost.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });

        currentY += 35;
      }

      // Add space before totals
      currentY += 20;

      // Check if we need a new page (leave space for subtotal and total sections)
      const remainingSpace = pageHeight - currentY;
      const neededSpace = 80; // Space needed for subtotal and total sections
      
      if (remainingSpace < neededSpace) {
        doc.addPage();
        currentY = 40; // Reset Y position on new page with some top margin
      }

      // Subtotal and total section
      doc.setDrawColor(mediumGray[0], mediumGray[1], mediumGray[2]);
      doc.setLineWidth(0.5);
      doc.line(110, currentY, pageWidth - 20, currentY);

      currentY += 18;

      // Subtotal
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text('Subtotal:', 140, currentY);
      doc.text(`$${totalCost.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });

      currentY += 20;

      // Total with black background
      doc.setFillColor(0, 0, 0);
      doc.rect(110, currentY - 10, pageWidth - 130, 25, 'F');

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('TOTAL:', 140, currentY + 5);
      doc.text(`$${totalCost.toFixed(2)}`, pageWidth - 25, currentY + 5, { align: 'right' });

      // Generate PDF blob
      const pdfBlob = doc.output('blob');
      const filename = `pager-invoice-${user.workspace_id || user.id}-${targetMonth}.pdf`;
      
      return { filename, blob: pdfBlob };
    } catch (error) {
      console.error('Error generating PDF for user:', user.email, error);
      return null;
    }
  }, [calculateEmailAccountsForMonth, calculateDomainsForMonth]);

  // Function to generate all invoices and package them into multiple zip files (5k users per zip)
  const generateAllInvoices = useCallback(async () => {
    if (!selectedMonth) {
      alert('Please select a month first');
      return;
    }

    setIsGeneratingAll(true);
    setBulkProgress({ current: 0, total: 0, currentUser: 'Initializing batch processing...' });
    setBatchProgress(null);

    try {
      const BATCH_SIZE = 5000; // 5k users per zip file
      let allUsers: ApiUser[] = [];
      let totalUsers = 0;
      
      if (bulkLimit === -1) { // All users - multi-batch processing
        setBulkProgress({ current: 0, total: 0, currentUser: 'Fetching all users...' });
        
        // First, get the total count
        const initialResponse = await apiService.getUsers({
          page: 1,
          limit: 1,
          search: '%', // Use wildcard to get ALL users, not just those with pending domains
        });
        
        const totalUsersCount = initialResponse.pagination.total;
        const FETCH_SIZE = 1000;
        const totalPages = Math.ceil(totalUsersCount / FETCH_SIZE);
        
        console.log('Initial API response pagination:', initialResponse.pagination);
        console.log(`Total users from API: ${totalUsersCount}`);
        console.log(`Total pages to fetch: ${totalPages}`);
        
        setBulkProgress({ 
          current: 0, 
          total: totalUsersCount, 
          currentUser: `Found ${totalUsersCount} total users. Fetching...` 
        });
        
        // Fetch all users page by page
        for (let page = 1; page <= totalPages; page++) {
          const response = await apiService.getUsers({
            page: page,
            limit: FETCH_SIZE,
            search: '%', // Use wildcard to get ALL users
          });
          
          console.log(`Page ${page} response:`, {
            dataLength: response.data.length,
            pagination: response.pagination
          });
          
          allUsers = [...allUsers, ...response.data];
          
          setBulkProgress({ 
            current: allUsers.length, 
            total: totalUsersCount, 
            currentUser: `Fetched ${allUsers.length} of ${totalUsersCount} users...` 
          });
        }
        
        console.log(`Final allUsers length: ${allUsers.length}`);
        totalUsers = allUsers.length;
      } else {
        // Single batch for limited users
        const response = await apiService.getUsers({
          page: 1,
          limit: bulkLimit,
          search: '',
        });
        allUsers = response.data;
        totalUsers = allUsers.length;
      }

      // Calculate number of batches needed
      const totalBatches = Math.ceil(totalUsers / BATCH_SIZE);
      const completedZips: string[] = [];
      
      console.log(`Total users to process: ${totalUsers}`);
      console.log(`Batch size: ${BATCH_SIZE}`);
      console.log(`Total batches: ${totalBatches}`);
      
      setBatchProgress({
        currentBatch: 0,
        totalBatches,
        batchSize: BATCH_SIZE,
        completedZips: []
      });

      // Process each batch
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIndex = batchIndex * BATCH_SIZE;
        const endIndex = Math.min(startIndex + BATCH_SIZE, totalUsers);
        const batchUsers = allUsers.slice(startIndex, endIndex);
        const actualBatchSize = batchUsers.length;

        console.log(`Processing batch ${batchIndex + 1}/${totalBatches}: ${actualBatchSize} users (indices ${startIndex}-${endIndex - 1})`);

        setBatchProgress(prev => prev ? {
          ...prev,
          currentBatch: batchIndex + 1
        } : null);

        setBulkProgress({ 
          current: 0, 
          total: actualBatchSize, 
          currentUser: `Starting batch ${batchIndex + 1}/${totalBatches} (${actualBatchSize} users)...` 
        });

        const zip = new JSZip();
        let processedCount = 0;
        let successfulCount = 0;

        // Process each user in the current batch
        for (const user of batchUsers) {
          setBulkProgress({ 
            current: processedCount + 1, 
            total: actualBatchSize, 
            currentUser: `Batch ${batchIndex + 1}: ${user.email}` 
          });

          try {
            // Fetch domains for this user
            const domainsResponse = await apiService.getUserDomains(user.id);
            const domains = domainsResponse.data || [];

            // Generate PDF for this user
            const pdfResult = await generateSingleInvoicePDF(user, domains, selectedMonth, processedCount + 1);

            if (pdfResult) {
              // Add PDF to zip
              zip.file(pdfResult.filename, pdfResult.blob);
              successfulCount++;
            }
          } catch (error) {
            console.error(`Failed to generate invoice for ${user.email}:`, error);
          }

          processedCount++;
        }

        // Generate zip file for this batch if we have any invoices
        if (successfulCount > 0) {
          setBulkProgress({ 
            current: actualBatchSize, 
            total: actualBatchSize, 
            currentUser: `Creating zip file for batch ${batchIndex + 1}...` 
          });

          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const [year, month] = selectedMonth.split('-');
          const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          const zipFilename = `pager-invoices-${monthName.replace(' ', '-')}-batch-${batchIndex + 1}-of-${totalBatches}-${successfulCount}-invoices.zip`;

          // Download the zip file
          const link = document.createElement('a');
          link.href = URL.createObjectURL(zipBlob);
          link.download = zipFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          completedZips.push(zipFilename);

          setBatchProgress(prev => prev ? {
            ...prev,
            completedZips: [...completedZips]
          } : null);

          // Add a small delay between downloads to avoid browser issues
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      const totalInvoices = completedZips.length;
      const totalProcessedUsers = totalBatches * BATCH_SIZE > totalUsers ? totalUsers : totalBatches * BATCH_SIZE;
      alert(`Successfully processed ${totalUsers} users across ${totalBatches} batch${totalBatches !== 1 ? 'es' : ''}.\n\n` +
            `Generated ${totalInvoices} zip file${totalInvoices !== 1 ? 's' : ''} with invoices for users with billable activity.\n\n` +
            `Downloaded files:\n${completedZips.map((name, i) => `${i + 1}. ${name}`).join('\n')}`);

    } catch (error) {
      console.error('Error generating all invoices:', error);
      alert(`Error generating invoices: ${error instanceof Error ? error.message : 'Unknown error'}. Please check the console for details.`);
    } finally {
      setIsGeneratingAll(false);
      setBulkProgress(null);
      setBatchProgress(null);
    }
  }, [selectedMonth, generateSingleInvoicePDF, bulkLimit]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchUserByEmail();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Mass Invoice Generator</h1>
        <p className="text-muted-foreground mt-2">
          Search for any user by email and download their invoice for a specific month.
        </p>
      </div>

      {/* Search Section */}
      <div className="bg-card rounded-lg border border-border p-6 space-y-4">
        <h2 className="text-xl font-semibold text-foreground flex items-center">
          <Search className="h-5 w-5 mr-2" />
          Search User
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Email Address</label>
            <div className="flex space-x-2">
              <input
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter user email address"
                className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button
                onClick={searchUserByEmail}
                disabled={isSearching || !searchEmail.trim()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSearching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span>{isSearching ? 'Searching...' : 'Search'}</span>
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Invoice Month</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {searchError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{searchError}</p>
          </div>
        )}
      </div>

      {/* User Details Section */}
      {foundUser && (
        <div className="bg-card rounded-lg border border-border p-6 space-y-4">
          <h2 className="text-xl font-semibold text-foreground flex items-center">
            <User className="h-5 w-5 mr-2" />
            User Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Email</label>
              <p className="text-foreground">{foundUser.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Workspace ID</label>
              <p className="text-foreground">{foundUser.workspace_id || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Client ID</label>
              <p className="text-foreground">{foundUser.client_id || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Platform</label>
              <p className="text-foreground">{foundUser.platform || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Total Domains</label>
              <p className="text-foreground">{userDomains.length}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Member Since</label>
              <p className="text-foreground">
                {new Date(foundUser.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>

          {domainsError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{domainsError}</p>
            </div>
          )}
        </div>
      )}

      {/* Invoice Preview Section */}
      {foundUser && selectedMonth && (
        <div className="bg-card rounded-lg border border-border p-6 space-y-4">
          <h2 className="text-xl font-semibold text-foreground flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Invoice Preview
          </h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-muted rounded-md">
              <span className="text-sm font-medium">Invoice Period:</span>
              <span className="text-sm">
                {new Date(selectedMonth + '-01').toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long'
                })}
              </span>
            </div>
            
            {/* Detailed Calculation Breakdown */}
            {calculationDetails && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 space-y-3">
                <h3 className="text-sm font-semibold text-blue-900">Calculation Breakdown</h3>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="font-medium">Total Domains:</span> {calculationDetails.totalDomains}
                  </div>
                  <div>
                    <span className="font-medium">Domains with Emails:</span> {calculationDetails.domainsWithEmails}
                  </div>
                  <div>
                    <span className="font-medium">Total Accounts to Create:</span> {calculationDetails.totalAccountsToCreate}
                  </div>
                  <div>
                    <span className="font-medium">Total Accounts to Cancel:</span> {calculationDetails.totalAccountsToCancel}
                  </div>
                  <div>
                    <span className="font-medium">Canceled Before Month:</span> {calculationDetails.accountsCanceledBeforeMonth}
                  </div>
                  <div>
                    <span className="font-medium">Canceled In Month:</span> {calculationDetails.accountsCanceledInMonth}
                  </div>
                </div>
                
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm font-medium text-blue-900">View Domain Details</summary>
                  <div className="mt-2 max-h-64 overflow-y-auto space-y-2">
                    {calculationDetails.details
                      .filter(d => d.accountsToCreate > 0 || d.accountsToCancel > 0)
                      .map((detail, index) => (
                      <div key={index} className="text-xs bg-white p-2 rounded border">
                        <div className="font-medium">{detail.domainName}</div>
                        <div>Created: {new Date(detail.created).toLocaleDateString()}</div>
                        <div>Accounts to Create: {detail.accountsToCreate}</div>
                        <div>Accounts to Cancel: {detail.accountsToCancel}</div>
                        {detail.canceledDate && (
                          <div>Canceled Date: {new Date(detail.canceledDate).toLocaleDateString()}</div>
                        )}
                        <div>Effective Count: <span className="font-medium">{detail.effectiveCount}</span></div>
                        <div className="text-gray-600">{detail.reason}</div>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-muted rounded-md">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Email Accounts:</span>
                  <span className="text-sm">{calculateEmailAccountsForMonth(userDomains, selectedMonth)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Rate:</span>
                  <span className="text-sm">$4.00 each</span>
                </div>
                <div className="flex justify-between items-center font-medium">
                  <span className="text-sm">Subtotal:</span>
                  <span className="text-sm">${(calculateEmailAccountsForMonth(userDomains, selectedMonth) * 4).toFixed(2)}</span>
                </div>
              </div>
              
              <div className="p-3 bg-muted rounded-md">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">New Domains:</span>
                  <span className="text-sm">{calculateDomainsForMonth(userDomains, selectedMonth)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Rate:</span>
                  <span className="text-sm">$12.00 each</span>
                </div>
                <div className="flex justify-between items-center font-medium">
                  <span className="text-sm">Subtotal:</span>
                  <span className="text-sm">${(calculateDomainsForMonth(userDomains, selectedMonth) * 12).toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between items-center p-4 bg-primary text-primary-foreground rounded-md font-bold">
              <span>Total Amount:</span>
              <span>
                ${(calculateEmailAccountsForMonth(userDomains, selectedMonth) * 4 + 
                   calculateDomainsForMonth(userDomains, selectedMonth) * 12).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={generateInvoicePDF}
              disabled={isGenerating}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isGenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span>{isGenerating ? 'Generating...' : 'Download Invoice PDF'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Download All Section */}
      {selectedMonth && (
        <div className="bg-card rounded-lg border border-border p-6 space-y-4">
          <h2 className="text-xl font-semibold text-foreground flex items-center">
            <Package className="h-5 w-5 mr-2" />
            Bulk Invoice Generation
          </h2>
          
          <p className="text-muted-foreground text-sm">
            Generate invoices for clients with billable activity for {(() => {
              const [year, month] = selectedMonth.split('-');
              return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long'
              });
            })()} and download them as a zip file.
          </p>

          {/* Bulk Limit Control */}
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-amber-900">User Limit</h3>
                <p className="text-xs text-amber-700">
                  Limit the number of users to process (recommended for large datasets)
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-amber-900">Limit:</label>
                <select
                  value={bulkLimit}
                  onChange={(e) => setBulkLimit(Number(e.target.value))}
                  disabled={isGeneratingAll}
                  className="px-2 py-1 border border-amber-300 rounded text-sm bg-white"
                >
                  <option value={10}>10 users</option>
                  <option value={25}>25 users</option>
                  <option value={50}>50 users</option>
                  <option value={100}>100 users</option>
                  <option value={250}>250 users</option>
                  <option value={500}>500 users</option>
                  <option value={1000}>1,000 users</option>
                  <option value={-1}>All Users (24k+) - Advanced</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-amber-600">
              ⚠️ With 24k+ total users, limiting to {bulkLimit === -1 ? 'ALL' : bulkLimit} users will process {bulkLimit === -1 ? 'all available' : `the first ${bulkLimit}`} users from the database. 
              {bulkLimit === -1 && (
                <>
                  <br />🚨 <strong>All Users mode</strong> creates multiple zip files (5,000 users per zip) and may take 30-60 minutes for 24k users.
                  <br />📦 Each zip file downloads automatically as it completes.
                </>
              )}
              <br />Use individual invoice generation for specific users.
            </p>
          </div>

          {/* Progress indicator */}
          {(bulkProgress || batchProgress) && (
            <div className="space-y-4">
              {/* Batch Progress */}
              {batchProgress && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-green-900">
                      Overall Progress: Batch {batchProgress.currentBatch} of {batchProgress.totalBatches}
                    </span>
                    <span className="text-sm text-green-700">
                      {Math.round((batchProgress.currentBatch / batchProgress.totalBatches) * 100)}%
                    </span>
                  </div>
                  
                  <div className="w-full bg-green-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(batchProgress.currentBatch / batchProgress.totalBatches) * 100}%` }}
                    ></div>
                  </div>
                  
                  <div className="text-xs text-green-700">
                    <div>Batch size: {batchProgress.batchSize.toLocaleString()} users per zip file</div>
                    {batchProgress.completedZips.length > 0 && (
                      <div className="mt-2">
                        <strong>Completed downloads:</strong>
                        <ul className="list-disc list-inside ml-2 max-h-20 overflow-y-auto">
                          {batchProgress.completedZips.map((zipName, index) => (
                            <li key={index} className="text-xs">{zipName}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Individual Batch Progress */}
              {bulkProgress && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-blue-900">
                      {batchProgress ? 'Current Batch' : 'Processing'}: {bulkProgress.current} of {bulkProgress.total}
                    </span>
                    <span className="text-sm text-blue-700">
                      {bulkProgress.total > 0 ? Math.round((bulkProgress.current / bulkProgress.total) * 100) : 0}%
                    </span>
                  </div>
                  
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0}%` }}
                    ></div>
                  </div>
                  
                  <p className="text-xs text-blue-700">
                    {bulkProgress.currentUser}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="text-sm text-muted-foreground">
              Will process {bulkLimit === -1 ? 'ALL users' : `the first ${bulkLimit} users`} and generate invoices only for those with billable activity.
              {bulkLimit === -1 && (
                <>
                  <br /><strong>Multi-zip mode:</strong> Creates separate zip files every 5,000 users with automatic downloads.
                  <br /><strong>Estimated:</strong> ~5 zip files for 24k users (depending on billable activity).
                </>
              )}
            </div>
            <button
              onClick={generateAllInvoices}
              disabled={isGeneratingAll || !selectedMonth}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isGeneratingAll ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Package className="h-4 w-4" />
              )}
              <span>{isGeneratingAll ? 'Generating...' : `Download ${bulkLimit === -1 ? 'All Users' : bulkLimit} Invoices`}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 